import { useCallback } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { useFocusEffect } from '@react-navigation/native';

export const useRedeemGiftCardAutoSubmit = (
  params: UseRedeemGiftCardAutoSubmitParams,
) => {
  const { giftCardCode, giftCardRegistrationCode, form, submitForm } = params;

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldAutoSubmit =
    typeof giftCardCode === 'string' && giftCardCode.length > 0;

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Auto-submits form if gift card redemption data is provided
   */
  const autoSubmitForm = useCallback(() => {
    if (!shouldAutoSubmit || form.formState.isSubmitting) return;

    form.setValue('giftCardCode', giftCardCode);
    form.setValue('giftCardRegistrationCode', giftCardRegistrationCode ?? '');

    void submitForm();
  }, [
    form,
    giftCardCode,
    giftCardRegistrationCode,
    shouldAutoSubmit,
    submitForm,
  ]);

  // Auto-submit form if the default gift card code is provided
  useFocusEffect(autoSubmitForm);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type RedeemGiftCardFormData = Readonly<{
  giftCardCode: string;
  giftCardRegistrationCode: string;
}>;

type RedeemGiftCardFormReturn = UseFormReturn<RedeemGiftCardFormData>;

type UseRedeemGiftCardAutoSubmitParams = {
  giftCardCode?: string | undefined;
  giftCardRegistrationCode?: string | undefined;
  form: RedeemGiftCardFormReturn;
  submitForm: () => Promise<void>;
};
